/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, FormikProps, FormikValues, useFormikContext } from 'formik';
import moment from 'moment';
import { ColorPalette } from 'theme/colors';
import * as yup from 'yup';

import { ReactComponent as GroupIcon } from 'assets/images/group.svg';
import { ReactComponent as UserIcon } from 'assets/images/user.svg';
import ListPreview from 'components/AggregationCards/ListPreview';
import SimpleCard from 'components/AggregationCards/SimpleAggregation';
import Header from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import SubmitButton from 'components/Buttons/SubmitButton';
import BottomSheet from 'components/ConfirmBottomSheet';
import Field from 'components/Field';
import FormikSubmitErrorEffect from 'components/FormikSubmitErrorEffect';
import Input from 'components/Input';
import Loader from 'components/Loader';
import MonthSelect from 'components/MonthSelect';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Section from 'components/Section';
import GenreSelector from 'components/Selects/GenreSelector';
import NationalitySelector from 'components/Selects/OnboardingSelect';
import useIsUserType from 'hooks/useIsAdmin';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';
import { AdminUser, AllowedUser } from 'models/adminUser';
import { NationalityCountry } from 'models/country';
import { Group } from 'models/group';
import { GroupedListModel } from 'models/groupedList';
import GroupSelectList from 'screens/components/GroupSelectList';
import ResponseScreen, { BackgroundTypes } from 'screens/components/ResponseScreen';
import { ActivateUserStatusNetactica, changeUserStatus, GetNetacticaUserByProfileId } from 'services/userService';
import { htmlT } from 'utils/generic';
import { extractErrorMessage, formatDateToBack } from 'utils/generic';
import { getLanguageRegion } from 'utils/i18n';

import GroupsAndUsersSelection from '../GroupsAndUsersSelection';
import AllowedUserSelect from './components/AllowedUserSelect';
import {
  Wrapper,
  FormContainer,
  Divider,
  GroupSelectLabel,
  ToggleCard,
  Checkbox,
  CheckboxLabel,
  TextTypeUser,
  UserGroupListContainer,
  Content,
  ErrorValidation,
  SectionDateOfBirth,
  BlockedUserAlert,
  UnlockUserButton,
} from './styles';

import { RestCountryResponse } from 'models/restCountry';
import PhoneInput from 'components/PhoneInput';
import { CountryCode } from 'components/Selects/CountryCodeSelect';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

interface UserFormProps {
  groupList: Group[];
  userId?: string;
  userList: AdminUser[];
  userData?: AdminUser;
  isUserLoading: boolean;
  country: string;
  companyId: string;
  id: string;
  saveUser: (user: AdminUser, companyId: string, id: string) => Promise<void>;
  fetchGroupList: () => Promise<void>;
  fetchUserList: () => Promise<void>;
  fetchCountryList: () => Promise<void>;
  loadingNationalityCountries: boolean;
  nationalityCountries: NationalityCountry[];
  hideWhatsapp: boolean;
  restCountries: RestCountryResponse[];
}

const USER_ICON = <UserIcon color={ColorPalette.HEX.DimGray} height={18} />;
const GROUP_ICON = <GroupIcon color={ColorPalette.HEX.DimGray} height={18} />;

const mapGroups = (groups: Group[]): GroupedListModel[] =>
  groups.map((group) => ({
    id: group.id,
    name: group.name,
    badge: !!group.rule ? { id: group.rule.id, name: group.rule.name } : { id: '', name: '' },
    entity: 'group',
    entityTypeImage: GROUP_ICON,
  }));

const mapUsers = (users: AdminUser[]): GroupedListModel[] =>
  users.map((user) => ({
    id: user.id || '',
    name: user.name,
    lastName: user.lastName,
    email: user.email,
    userGroupId: user.userGroupId || '',
    userGroupName: user.userGroupName || '',
    entity: 'user',
    entityTypeImage: USER_ICON,
  }));

const UsersForm = ({
  userData,
  userId,
  isUserLoading,
  groupList,
  userList,
  country,
  saveUser,
  companyId,
  id,
  fetchCountryList,
  fetchGroupList,
  fetchUserList,
  loadingNationalityCountries,
  nationalityCountries,
  hideWhatsapp,
  restCountries,
}: UserFormProps) => {
  const history = useHistory();
  const { t } = useStoreTranslation();
  const [data, setData] = useState<AdminUser>();
  const formikRef = useRef<FormikProps<AdminUser>>(null);
  const [isGroupFetching, setGroupFetching] = useState(false);
  const [isUserFetching, setUserFetching] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isGroupSelectionVisible, setGroupSelectionVisible] = useState(false);
  const [isGroupAndUsersSelectionVisible, setGroupsAndUsersSelectionVisible] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<GroupedListModel[]>([]);
  const selectedGroup = selectedGroups.length > 0 ? selectedGroups[0] : null;
  const [existingRecord, setExistingRecord] = useState(false);
  const toast = useToast();
  const stringGroup = userData?.id ? 'userForm.update' : 'userForm.create';
  const groupListSelect = useMemo(() => mapGroups(groupList), [groupList]);
  const userListSelect = useMemo(() => mapUsers(userList), [userList]);
  const [month, setMonth] = useState<number>(0);
  
  const loggedUser = useUserAccount();
  const isAdmin = useIsUserType('admin');
  const isSuperAdmin = useIsUserType('superAdmin') || useIsUserType('supportImplementation');
  const companyConfiguration = useSelector((state: RootState) => state.companyConfiguration.configuration);

  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [isUserDisabled, setIsUserDisabled] = useState(false);

  const fetchUserIsActiveByNetactica = async () => {
    try {
      const userResponse = await GetNetacticaUserByProfileId(userData?.profileId || 0);
      
      if (userResponse) {
        setIsUserLoaded(true);
        console.log(userResponse.user.active);
        
        setIsUserDisabled(!userResponse.user.active);
      }
    } catch (error) {
      // handle error
    }
  };

  const handleUnlockUser = async () => {
    try {      
      const response = await ActivateUserStatusNetactica(userData?.username || userData?.email, companyId);
      setIsUserDisabled(false);
      
      if (!response) { throw new Error('Error unlocking user'); }

      toast.add({
        type: 'success',
        message: t('userForm.accessSystem.userBlockedSuccessToast'),
      });
    } catch (error) {
      toast.add({
        type: 'danger',
        message: t('userForm.accessSystem.userBlockedErrorToast'),
      });
    }
  };
    
  const handleSelectGroup = (group: GroupedListModel[]) => {
    setGroupSelectionVisible(false);
    setSelectedGroups(group);
  };

  useEffect(() => {
    const loadGroups = async () => {
      await fetchGroupList();
      setGroupFetching(false);
    };
    loadGroups();
  }, [fetchGroupList]);

  useEffect(() => {
    const loadUsers = async () => {
      await fetchUserList();
      setUserFetching(false);
    };
    loadUsers();
  }, [fetchUserList]);

  useEffect(() => {
    if (!nationalityCountries.length && !loadingNationalityCountries) {
      fetchCountryList();
    }
  }, [fetchCountryList, loadingNationalityCountries, nationalityCountries, userData]);

  useEffect(() => {
    if (userData?.id) {
      setExistingRecord(true);
      setData(userData);
      fetchUserIsActiveByNetactica();
      //setPhonePrefix(userData?.phonePrefix);

      if (userData?.birthDate !== '0001-01-01T00:00:00Z') {
        setMonth(parseInt(moment.utc(userData?.birthDate).format('MM')));
      }
    }

    if (userData) {
      const userGroup = groupList.find((group) => group.id === userData.userGroupId);
      setSelectedGroups(userGroup ? mapGroups([userGroup]) : []);
    }
  }, [userData, groupList]);

  const handleConfirm = (values: AdminUser) => {
    let formattedMonth;
    let birthDate = '';

    if (isDateOfBirthValid()) {
      if (values.birthDate_day === '' && values.birthDate_year === '') {
        values.birthDate_day = '01';
        formattedMonth = '01';
        values.birthDate_year = '1980';
      }
      formattedMonth = month && month < 10 ? '0' + month : month;
      birthDate = `${values.birthDate_day}/${formattedMonth}/${values.birthDate_year}`;
    } else {
      setConfirm(false);
      return;
    }

    const newData: AdminUser = {
      ...data,
      ...values,
      isInvite: data?.isInvite === false ? false : true,
      userGroupId: selectedGroup?.id,
      country: getLanguageRegion(country),
      birthDate: month ? formatDateToBack(birthDate) : '',
      idRappi: userData?.idRappi,
      companyId,
    };

    setData(newData);
    setConfirm(true);
  };

  const save = async () => {
    try {
      setLoading(true);
      await saveUser(
        {
          ...data,
          email: data?.email?.toLowerCase(),
          usersBookAllowed: data?.usersBookAllowed?.filter((user) => user.id !== (userId || '')),
        } as AdminUser,
        companyId,
        id,
      );

      //Desactiva el usuario si vino activo y se pidió para desactivar
      if (isAdmin || isSuperAdmin) {
        await changeUserStatus(data.id, data.email, data.isActive, data.companyId);
      }

      setConfirm(false);
      setShowSuccess(true);
    } catch (error) {
      setConfirm(false);
      window.scrollTo(0, 0);
      toast.add({
        type: 'danger',
        message: extractErrorMessage(error) !== 'NetacticaUserCreateError' ? extractErrorMessage(error) || t('defaultError.default') : t('defaultError.defaultUser'),
      });
    } finally {
      setLoading(false);
    }
  };

  const containsSequentialNumbers = (str: string): boolean => {
    for (let i = 0; i < str.length - 1; i++) {
      const current = parseInt(str[i]);
      const next = parseInt(str[i + 1]);

      if (!isNaN(current) && !isNaN(next)) {
        if (next === current + 1 || next === current - 1) {
          return true;
        }
      }
    }
    return false;
  };

  const validationSchema = yup.object().shape({
    name: yup.string().trim().max(100).required(),
    lastName: yup.string().trim().max(100).required(),
    username: yup.string().trim().max(100).required(),
    password: yup
    .string()
    .test('password-validation', 'Senha inválida', function (value) {
      
      if (existingRecord) return true;

      const password = value || '';
      const hasLength = password.length >= 8 && password.length <= 12;
      const hasSpecialChar = /[!¡”#$%/,*.@&()\-_=+{}\[\]|':;¿?<>\^]/.test(password);
      const hasUpperLower = /[a-z]/.test(password) && /[A-Z]/.test(password);
      const hasNumber = /\d/.test(password);
      const hasNoConsecutive = !/(\d)\1{1,}/.test(password);
      const hasNoSequentialNumbers = !containsSequentialNumbers(password);
      const hasUserInfo = validatePasswordContainsUserInfo(password);

      const isSsoCorporate = companyConfiguration?.loginSso?.isSsoCorporate ?? false;

      if (isSsoCorporate) {
        return hasLength && hasUpperLower && hasNumber;
      }

      return hasLength && hasSpecialChar && hasUpperLower && hasNumber && hasNoConsecutive && hasNoSequentialNumbers && hasUserInfo;
    }),
    phoneNumber: yup.string().phone().required(),
    phonePrefix: yup.string().required(),
    email: yup.string().email().noPlusSign().required(),
    gender: yup.string(),
    nationality: yup.string().required(),
    birthDate_day: yup.number().positive().moreThan(0).lessThan(32, t('guestForm.day.invalidNumber')).required(),
    birthDate_month: yup.number().positive().moreThan(0).lessThan(13, t('guestForm.month.invalidNumber')).required(),
    birthDate_year: yup
      .number()
      .moreThan(1910)
      .lessThan(new Date().getFullYear() + 1, t('guestForm.year.invalidNumber')).required(),
    documentNumber: yup.string().trim().max(100).required(),
    passportNumber: yup.string().trim().max(100),
    type: yup.string().required(),
    sendWhatsapp: yup.boolean().required(),
    userGroupId: yup.string().required(),
    isTravelArranger: yup.bool().required(),
    isGuestBookAllowed: yup.bool().when('isTravelArranger', {
      is: true,
      then: yup.bool().required(),
    }),
    isAllUsersBookAllowed: yup.bool().when('isTravelArranger', {
      is: true,
      then: yup.bool().required(),
    }),
    groupsBookAllowed: yup
      .array()
      .test('travelArranger-groups', 'userForm.travelerUsersAllowed.validation', function (value) {
        const { isTravelArranger, isAllUsersBookAllowed, usersBookAllowed } = (this as yup.TestContext).parent;

        if (
          isTravelArranger &&
          isAllUsersBookAllowed === false &&
          usersBookAllowed?.length === 0 &&
          value?.length === 0
        )
          return false;
        return true;
      }),
    usersBookAllowed: yup
      .array()
      .test('travelArranger-users', 'userForm.travelerUsersAllowed.validation', function (value) {
        const { isTravelArranger, isAllUsersBookAllowed, groupsBookAllowed } = (this as yup.TestContext).parent;

        if (
          isTravelArranger &&
          isAllUsersBookAllowed === false &&
          groupsBookAllowed?.length === 0 &&
          value?.length === 0
        )
          return false;
        return true;
      }),
    isActive: yup.boolean().required(),
  });

  const currentUserAsAllowedTraveler: AllowedUser & {
    disabled: boolean;
    userGroupId?: string;
    userGroupName?: string;
  } = {
    id: userData?.id || '',
    name: userData?.name || t('userForm.userDefaultValue'),
    lastName: userData?.lastName || '',
    email: userData?.email || '',
    disabled: true,
    userGroupId: userData?.userGroupId,
    userGroupName: userData?.userGroupName,
  };

  const usersBookAllowedWithCurrentUser = [
    currentUserAsAllowedTraveler,
    ...(userData?.usersBookAllowed || []).filter((user) => user.id !== currentUserAsAllowedTraveler.id),
  ];

  const initValues: AdminUser = {
    id: userData?.id || '',
    name: userData?.name || '',
    username: userData?.username || userData?.email || '', 
    password: userData?.password || '',
    lastName: userData?.lastName || '',
    phoneNumber: userData?.phoneNumber || '',
    phonePrefix: userData?.phonePrefix || '',
    email: userData?.email || '',
    birthDate: userData?.birthDate === '0001-01-01T00:00:00Z' ? '' : userData?.birthDate || '',
    birthDate_day:
      userData?.birthDate === '0001-01-01T00:00:00Z' || userData?.birthDate === undefined
        ? ''
        : moment.utc(userData?.birthDate)?.format('DD') || '',
    birthDate_month:
      userData?.birthDate === '0001-01-01T00:00:00Z' || userData?.birthDate === undefined
        ? ''
        : moment.utc(userData?.birthDate)?.format('MM') || '',
    birthDate_year:
      userData?.birthDate === '0001-01-01T00:00:00Z' || userData?.birthDate === undefined
        ? ''
        : moment.utc(userData?.birthDate)?.format('YYYY') || '',
    gender: userData?.gender || 'Male',
    nationality: userData?.nationality || '',
    documentNumber: userData?.documentNumber || '',
    passportNumber: userData?.passportNumber || '',
    type: userData?.type || 'traveler',
    userGroupId: userData?.userGroupId === '00000000-0000-0000-0000-000000000000' ? '' : userData?.userGroupId,
    isTravelArranger: userData?.isTravelArranger || false,
    isGuestBookAllowed: userData?.isGuestBookAllowed || false,
    isAllUsersBookAllowed: userData?.isAllUsersBookAllowed || false,
    groupsBookAllowed:
      userData?.groupsBookAllowed?.map((item) => ({ ...item, entity: 'group', entityTypeImage: GROUP_ICON })) || [],
    usersBookAllowed:
      usersBookAllowedWithCurrentUser.map((item) => ({ ...item, entity: 'user', entityTypeImage: USER_ICON })) || [],
    sendWhatsapp: userData?.sendWhatsapp || false,
    isActive: userData?.isActive ?? true,
  };

  const isEmailDisabled = () => {
    return false; 
    if (!data) return false;
    if (!data.email) return false;
    if (data.isInvite) return false;

    return true;
  };

  const handleChangeMonth = (month: number) => {
    setMonth(month);
  };

  const isDateOfBirthValid = () => {
    const birthDate_day = (document.getElementById('input_birthDate_day') as HTMLInputElement)?.value || '';
    const birthDate_year = (document.getElementById('input_birthDate_year') as HTMLInputElement)?.value || '';

    if (birthDate_day === '' && birthDate_year === '') {
      return true;
    }

    return moment(`${birthDate_year}-${month}-${birthDate_day}`, 'YYYY-MM-DD').isValid();
  };

  const validatePassword = (password: string) => {
    const specialChars = /[!¡”#$%/,*.@&()\-_=+{}\[\]|':;¿?<>\^`]/;

    const isEmpty = password.trim().length === 0;
    const isSsoCorporate = companyConfiguration?.loginSso?.isSsoCorporate ?? false;

    const validations = [
      {
        name: 'length',
        title: t('userForm.accessSystem.length.title'),
        message: t('userForm.accessSystem.length.message'),
        valid: isEmpty ? false : password.length >= 8 && password.length <= 12
      },
      ...(!isSsoCorporate ? [{
        name: 'special-char',
        title: t('userForm.accessSystem.specialChar.title'),
        message: t('userForm.accessSystem.specialChar.message'),
        valid: isEmpty ? false : specialChars.test(password)
      }] : []),
      {
        name: 'uppercase-char',
        title: t('userForm.accessSystem.uppercaseChar.title'),
        message: t('userForm.accessSystem.uppercaseChar.message'),
        valid: isEmpty ? false : /[a-z]/.test(password) && /[A-Z]/.test(password)
      },
      {
        name: 'numeric-char',
        title: t('userForm.accessSystem.numericChar.title'),
        message: t('userForm.accessSystem.numericChar.message'),
        valid: isEmpty ? false : /\d/.test(password)
      },
      ...(!isSsoCorporate ? [
        {
          name: 'consecutive-char',
          title: t('userForm.accessSystem.consecutiveChar.title'),
          message: t('userForm.accessSystem.consecutiveChar.message'),
          valid: isEmpty ? false : (!/(\d)\1{1,}/.test(password) && !containsSequentialNumbers(password))
        },
        {
          name: 'profile-date',
          title: t('userForm.accessSystem.profileData.title'),
          message: t('userForm.accessSystem.profileData.message'),
          valid: isEmpty ? false : validatePasswordContainsUserInfo(password)
        }
      ] : [])
    ];

    return validations;
  };
  
  const transformedCountries: CountryCode[] = restCountries
        .filter((c) => c.callingCodes?.[0])
        .map((c) => ({
          code: `+${c.callingCodes[0]}`,
          name: c.name,
          id: c.alpha2Code,
          flag: c.flag 
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

  const getSelectedCountry = () => {
    
    if (userData && userData.phonePrefix && userData.phonePrefix !== '') {
      return userData.phonePrefix;
    }
    if (!userData) {
      return transformedCountries.find((country) => country.id === companyConfiguration.countryCode)?.code;
    }

    return '';
  }

  const selectedCountry = getSelectedCountry();
  
  const validatePasswordContainsUserInfo = (
    password: string
  ) => {
    
    if (!password) return false;

    // campos para validar
    const userValues = [
      formikRef.current?.values?.name,
      formikRef.current?.values?.lastName,
      formikRef.current?.values?.username,
      formikRef.current?.values?.email,
      formikRef.current?.values?.phoneNumber,
      formikRef.current?.values?.documentNumber,
      formikRef.current?.values?.passportNumber
    ].filter(Boolean); // Remove null/undefined

    const validation = userValues.some((val) => 
      password.sanitize().toLocaleLowerCase().includes(val.sanitize().toLocaleLowerCase())
    );

    return !validation;
  };

  const PasswordValidation = () => {

    const { values } = useFormikContext<{ password: string }>();
    
    const [rules, setRules] = useState(() => validatePassword(''));
  
    useEffect(() => {
      setRules(validatePassword(values.password || ''));
    }, [values.password]);
  
    return (
      <div style={{ marginTop: 8 }}>
        {values.password && rules.map(rule => (
          <div key={rule.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {rule.valid ? (
              <span style={{ color: 'green' }}>✔</span>
            ) : (
              <span style={{ color: 'red' }}>✖</span>
            )}
            <span style={{ fontSize: '12px', paddingTop: '3px' }}><b>{rule.title}: </b>{rule.message}</span>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <Wrapper className="target">
      {isUserLoading || loadingNationalityCountries ? (
        <Loader fullScreen />
      ) : (
        <>
          <Mobile>
            <Header goBack={() => history.push('/corporate/usersList')}>{t(`${stringGroup}.title`)}</Header>
          </Mobile>

          <Desktop>
            <ScreenHeader title={t('userMenu.usersProfilesTitle')} subtitle={t('userMenu.usersProfilesDetail')} />
          </Desktop>
          <Formik
            innerRef={formikRef}
            initialStatus={{ rightCheckIfIsValid: true }}
            initialValues={initValues}
            validationSchema={validationSchema}
            onSubmit={(value: AdminUser) => handleConfirm(value)}
            isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
          >
            {({
              values,
              setValues,
              dirty,
              setFieldValue,
              errors,
              submitCount,
              isValid,
              validateField,
            }: FormikValues) => (
              <>
                <FormikSubmitErrorEffect />

                <FormContainer $spacingBottom={!!userData?.id && dirty}>
                  <Section
                    title={t('userForm.basicInformations.title')}
                    details={t('userForm.basicInformations.description')}
                  >
                    <Field name="name" label={t('userForm.userName')}>
                      <Input
                        placeholder={t('userForm.firstnamePlaceHolder')}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue(
                            'usersBookAllowed',
                            values.usersBookAllowed.map((user) => {
                              if (user.id !== (userData?.id || '')) return user;
                              return { ...user, name: event.target.value || t('userForm.userDefaultValue') };
                            }),
                          )
                        }
                      />
                    </Field>
                    <Field name="lastName" label={t('userForm.userLastName')}>
                      <Input
                        placeholder={t('userForm.lastnamePlaceHolder')}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue(
                            'usersBookAllowed',
                            values.usersBookAllowed.map((user) => {
                              if (user.id !== (userData?.id || '')) return user;
                              return {
                                ...user,
                                lastName: event.target.value,
                                name:
                                  !event.target.value && !user.name
                                    ? t('userForm.userDefaultValue')
                                    : user.name === t('userForm.userDefaultValue')
                                    ? ''
                                    : user.name,
                              };
                            }),
                          )
                        }
                      />
                    </Field>

                    <Field name="email" label={t('userForm.email')}>
                      <Input
                        id="email"
                        type="email"
                        placeholder={t('userForm.emailPlaceHolder')}
                        disabled={isEmailDisabled()}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue(
                            'usersBookAllowed',
                            values.usersBookAllowed.map((user) => {
                              if (user.id !== (userData?.id || '')) return user;
                              return {
                                ...user,
                                email: event.target.value,
                              };
                            }),
                          )
                        }
                      />
                    </Field>

                    <Field name="phoneNumber" label={t('userForm.userPhoneNumber')}>
                      <PhoneInput
                        mask={'9'.repeat(50)}
                        phoneField="phoneNumber"
                        countryCodeField="phonePrefix"
                        countryCodes={transformedCountries}
                        selectedCountry={getSelectedCountry()}
                      />
                    </Field>

                    <Field name="gender" label={t('userForm.gender')}>
                      <GenreSelector id="gender" />
                    </Field>                    
                    
                    

                  </Section>

                  <SectionDateOfBirth>
                    <div></div>
                    <Content>
                      <Field name="birthDate_day" label={t('guestForm.day.label')}>
                        <Input
                          type="text"
                          id="input_birthDate_day"
                          mask="99"
                          inputMode="numeric"
                          placeholder={t('guestForm.day.placeholder')}
                        />
                      </Field>
                      <Field name="birthDate_month">
                        <MonthSelect
                          handleChangeDate={handleChangeMonth}
                          initialValue={month?.toString()}
                          name="input_birthDate_month"
                        />
                      </Field>
                      <Field name="birthDate_year" label={t('guestForm.year.label')}>
                        <Input
                          type="text"
                          id="input_birthDate_year"
                          mask="9999"
                          inputMode="numeric"
                          placeholder={t('guestForm.year.placeholder')}
                        />
                      </Field>
                      {!isDateOfBirthValid() && (
                        <>
                          <ErrorValidation>{t('guestForm.dateOfBirthInvalid')}</ErrorValidation>
                        </>
                      )}
                      <div></div>
                    </Content>
                  </SectionDateOfBirth>
                  
                  <Divider />

                  <Section
                    title={t('userForm.accessSystem.title')}                    
                  >
                    <Field name="username" label={t('userForm.accessSystem.username')}>
                      <>
                        <Input
                          disabled={existingRecord}
                          placeholder={t('userForm.accessSystem.usernamePlaceholder')}
                          value={values.username}
                          onChange={(e) => setFieldValue('username', e.target.value)}
                        />
                        {isUserLoaded && isUserDisabled && (
                          <>
                            <BlockedUserAlert>
                              <>⚠️ </> 
                              {t('userForm.accessSystem.userBlocked')}
                            </BlockedUserAlert>

                            <UnlockUserButton type='button' onClick={handleUnlockUser}>
                              {t('userForm.accessSystem.unlockUser')}
                            </UnlockUserButton>
                          </>
                        )}
                      </>
                      
                    </Field>
                    {!existingRecord && (<fieldset>
                      <Field  name="password" label={t('userForm.accessSystem.password')}>
                        <Input placeholder={t('userForm.accessSystem.passwordPlaceholder')} />
                      </Field>
                      <PasswordValidation />
                    </fieldset>)}

                  </Section>

                  <Divider />

                  <Section title={t('userForm.travelInformations.title')}>
                    
                    <Field name="documentNumber" label={t('userForm.documentNumber')}>
                      <Input 
                        id="documentNumber" 
                        placeholder={t('userForm.documentPlaceholder')} 
                        onChange={(e) => {
                          e.currentTarget.value = e.currentTarget.value.replace(/\s/g, '');
                        }} />
                    </Field>

                    <Field name="passportNumber" label={t('userForm.passportNumber')}>
                      <Input 
                        id="passportNumber" 
                        placeholder={t('userForm.passportPlaceholder')} 
                        onChange={(e) => {
                        e.currentTarget.value = e.currentTarget.value.replace(/\s/g, '');
                      }} />
                    </Field>

                    <Field name="nationality" label={t('userForm.country')}>
                      <NationalitySelector
                        type="nationality"
                        options={nationalityCountries}
                        title={t('userForm.countrySelect')}
                        enableSearch={true}
                      />
                    </Field>

                    <div>
                      <GroupSelectLabel isError={submitCount > 0 && !!errors.userGroupId}>
                        {t('userForm.addGroupLabel')}
                      </GroupSelectLabel>
                      <SimpleCard
                        title={selectedGroup?.name}
                        placeholder={t('userForm.addGroup')}
                        icon="user"
                        active={!!selectedGroup}
                        onClick={() => setGroupSelectionVisible(true)}
                        onAdd={() => setGroupSelectionVisible(true)}
                        errorMessage={submitCount > 0 ? errors.userGroupId : ''}
                      />
                      <ApplicationOverlayContent
                        sidebar
                        show={isGroupSelectionVisible}
                        onClose={() => setGroupSelectionVisible(false)}
                      >
                        <GroupSelectList
                          noAdd
                          singleSelect
                          isAlphabetic
                          showGroupsSelection
                          groupList={groupListSelect}
                          currentGroup={selectedGroups}
                          isLoading={isGroupFetching}
                          tourHasAccess
                          tourLoading={false}
                          onConfirmSelection={(group: GroupedListModel[]) => {
                            handleSelectGroup(group);
                            setFieldValue('userGroupId', group[0]?.id);
                            setFieldValue(
                              'usersBookAllowed',
                              values.usersBookAllowed.map((user) => {
                                if (user.id !== (userData?.id || '')) return user;
                                return { ...user, userGroupId: group[0]?.id, userGroupName: group[0]?.name };
                              }),
                            );
                          }}
                          onCloseSelectionScreen={() => setGroupSelectionVisible(false)}
                          changeModalTitleKey="userForm.group.bottomSheetTitle"
                          changeModalMessageKey="userForm.group.userHasGroup"
                        />
                      </ApplicationOverlayContent>
                    </div>
                  </Section>

                  <Divider />

                  <Section title={t('userForm.permissions.title')} details={t('userForm.permissions.description')}>
                    <ToggleCard
                      title={t('userForm.adminTitle')}
                      message={t('userForm.adminDescription')}
                      value={values.type.toLowerCase() === 'admin'}
                      onToggle={(_, value) => setFieldValue('type', value ? 'admin' : 'traveler')}
                      isDisabledToggle={loggedUser.type.toLowerCase() !== 'admin' && loggedUser.type.toLowerCase() !== 'superadmin' && loggedUser?.type.toLocaleLowerCase() !== 'supportimplementation'}
                      errorMessage={loggedUser.type.toLowerCase() !== 'admin' && loggedUser.type.toLowerCase() !== 'superadmin' && loggedUser?.type.toLocaleLowerCase() !== 'supportimplementation' ? t('userForm.userTypeDoesNotAllow') : ''}
                      hide={false}
                    />
                    <ToggleCard
                      title={t('userForm.travelerTitle')}
                      message={t('userForm.travelerDescription')}
                      value={values.isTravelArranger}
                      onToggle={(_, value) => setFieldValue('isTravelArranger', value)}
                      errorMessage={submitCount > 0 ? errors.usersBookAllowed || errors.groupsBookAllowed : ''}
                      hide={false}
                    >
                      {values.isTravelArranger && (
                        <>
                          <Field name="isAllUsersBookAllowed" label={t('userForm.travelerUsersAllowed.label')}>
                            <AllowedUserSelect
                              id="isAllUsersBookAllowed"
                              onValueChange={(value) => {
                                if (!value) setGroupsAndUsersSelectionVisible(true);
                              }}
                            />
                          </Field>
                          {values.isAllUsersBookAllowed !== undefined && (
                            <Checkbox
                              checked={values.isGuestBookAllowed}
                              onChange={(value) => {
                                setFieldValue('isGuestBookAllowed', value);
                              }}
                              size={24}
                            >
                              <CheckboxLabel
                                onClick={() => {
                                  setFieldValue('isGuestBookAllowed', !values.isGuestBookAllowed);
                                }}
                              >
                                {t('userForm.travelerUsersAllowed.allowInvites')}
                              </CheckboxLabel>
                            </Checkbox>
                          )}

                          {values.isAllUsersBookAllowed === false && (
                            <>
                              <UserGroupListContainer>
                                <ListPreview
                                  placeholder={t('userForm.travelerUsersAllowed.placeholder')}
                                  icon="user"
                                  onAdd={() => setGroupsAndUsersSelectionVisible(true)}
                                  onClick={() => setGroupsAndUsersSelectionVisible(true)}
                                  previewItems={[...values.usersBookAllowed, ...values.groupsBookAllowed].map(
                                    (item) => ({
                                      title: `${item.name} ${item.lastName || ''}`,
                                      subtitle: item.entity === 'user' ? item.email : '',
                                      iconType: item.entity === 'user' ? 'initials' : 'group',
                                    }),
                                  )}
                                  noBorder
                                />
                              </UserGroupListContainer>
                              <ApplicationOverlayContent
                                sidebar
                                show={isGroupAndUsersSelectionVisible}
                                onClose={() => setGroupsAndUsersSelectionVisible(false)}
                              >
                                <GroupsAndUsersSelection
                                  userList={userListSelect}
                                  selectedUsers={values.usersBookAllowed}
                                  groupList={groupListSelect}
                                  selectedGroups={values.groupsBookAllowed}
                                  isLoading={isGroupFetching || isUserFetching}
                                  onConfirmSelection={(items: GroupedListModel[]) => {
                                    setValues({
                                      ...values,
                                      usersBookAllowed: items.filter((item) => item.entity === 'user'),
                                      groupsBookAllowed: items.filter((item) => item.entity === 'group'),
                                    });
                                  }}
                                  onCloseSelectionScreen={() => setGroupsAndUsersSelectionVisible(false)}
                                />
                              </ApplicationOverlayContent>
                            </>
                          )}
                        </>
                      )}
                    </ToggleCard>

                    <ToggleCard
                      title={t('userForm.whatsAppTitle')}
                      message={t('userForm.whatsAppDescription')}
                      value={values.sendWhatsapp}
                      onToggle={(_, value) => setFieldValue('sendWhatsapp', value ? true : false)}
                      errorMessage={errors.phoneNumber ? t('error.whatsappError') : ''}
                      isDisabledToggle={errors.phoneNumber}
                      hide={hideWhatsapp}
                    />

                    <ToggleCard
                      title={t(values.isActive ? 'userForm.statusActiveTitle' : 'userForm.statusDesactivatedTitle')}
                      message={
                        htmlT(
                          t,
                          values.isActive ? 'userForm.statusActive' : 'userForm.statusDesactivated',
                        ) as unknown as string
                      }
                      value={values.isActive}
                      onToggle={(_, value) => setFieldValue('isActive', value ? true : false)}
                      isDisabledToggle={values.id === '' || (!isAdmin && !isSuperAdmin)}
                      hide={false}
                    />
                  </Section>

                  <SubmitButton dirty={dirty} isEdit={!!userData?.id} loading={isLoading}>
                    {t(`${stringGroup}.confirmButton`)}
                  </SubmitButton>
                </FormContainer>
              </>
            )}
          </Formik>
          <BottomSheet
            show={confirm}
            title={t(`${stringGroup}.modalConfirmTitle`)}
            content={
              data?.type === 'admin'
                ? t(`${stringGroup}.modalConfirmAdmText`)
                : t(`${stringGroup}.modalConfirmTravelerText`)
            }
            onClose={() => setConfirm(false)}
            onConfirm={save}
          />
          <ResponseScreen
            text={data?.id ? t('usersSuccess.edit') : t('usersSuccess.text')}
            subText={
              <>
                {htmlT(t, data?.id ? 'usersSuccess.editSubText' : 'usersSuccess.subText')}
                <TextTypeUser>{data?.email?.toLowerCase()}</TextTypeUser>
              </>
            }
            background={BackgroundTypes.Mountain}
            buttonOnClick={() => history.push('/corporate/usersList')}
            show={showSuccess}
          />
        </>
      )}
    </Wrapper>
  );
};

export default UsersForm;
